<template>
  <div>
    <report-table
      :data="filteredData"
      :loading="isLoading"
      :search-query="searchQuery"
      :changing-status-order-id="changingStatusOrderId"
      @show-details="showOrderDetails($event.order, $event.guest)"
      @change-status="changeOrderStatus($event.order, $event.guest, $event.newStatus)"
    />

    <!-- Order Details Dialog -->
    <guest-order-details-dialog
      v-model="orderDetailsDialog"
      :order="selectedOrder"
      :guest="selectedGuest"
      :changing-status-order-id="changingStatusOrderId"
      @change-status="changeOrderStatus($event.order, $event.guest, $event.newStatus)"
    />
  </div>
</template>

<script>
import ReportTable from "@/components/guest/ReportTable.vue";
import reportService from "@/services/report";
import GuestOrderDetailsDialog from "@/components/dialogs/GuestOrderDetailsDialog.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "ReportTab",
  components: {
    ReportTable,
    GuestOrderDetailsDialog,
  },
  mixins: [hasSnackbar, hasPrinting, hasExcel],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: "",
    },
    dateRange: {
      type: Array,
      default: () => [
        new Date().toISOString().substr(0, 10),
        new Date().toISOString().substr(0, 10),
      ],
    },
    selectedStatus: {
      type: Object,
      default: () => ({ id: 0, name: "all" }),
    },
    selectedDeliveryType: {
      type: Object,
      default: () => ({ id: 3, name: "all" }),
    },
    isActiveTab: Boolean,
  },
  data() {
    return {
      reportData: {},
      isLoading: false,
      orderDetailsDialog: false,
      selectedOrder: null,
      selectedGuest: null,
      changingStatusOrderId: null,
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false, width: "70px" },
        {
          text: this.$t("name_surname_phone"),
          value: "nameAndType",
          sortable: true,
        },
        { text: this.$t("address"), value: "address", sortable: false },
        { text: this.$t("time"), value: "time", sortable: false, width: "80px" },
        { text: this.$t("ordered"), value: "orders", sortable: false },
        { text: this.$t("total"), value: "total", sortable: false, width: "80px" },
        { text: this.$t("status"), value: "status", sortable: false, width: "120px" },
      ];
    },
    processedData() {
      // API vraća: { companyName: { date: [guests] } }
      // Treba nam: { date: [guests] } - spajamo sve kompanije
      const result = {};

      if (!this.reportData) return result;

      Object.values(this.reportData).forEach((companyData) => {
        Object.entries(companyData).forEach(([date, guests]) => {
          if (!result[date]) {
            result[date] = [];
          }
          result[date].push(...guests);
        });
      });

      // Sortiraj datume desc
      const sorted = {};
      Object.keys(result)
        .sort((a, b) => new Date(b) - new Date(a))
        .forEach((key) => {
          sorted[key] = result[key];
        });

      return sorted;
    },
    filteredData() {
      if (!this.processedData) return {};

      const filtered = {};

      Object.entries(this.processedData).forEach(([date, guests]) => {
        const filteredGuests = guests.filter((guest) => {
          const order = guest.orders_guest && guest.orders_guest[0];
          if (!order) return false;

          // Filter by status
          if (this.selectedStatus && this.selectedStatus.id !== 0) {
            if (
              order.state !== this.selectedStatus.alias &&
              order.state !== this.selectedStatus.name
            ) {
              return false;
            }
          }

          // Filter by delivery type
          if (this.selectedDeliveryType && this.selectedDeliveryType.id !== 3) {
            if (order.delivery_type !== this.selectedDeliveryType.id) {
              return false;
            }
          }

          return true;
        });

        if (filteredGuests.length > 0) {
          filtered[date] = filteredGuests;
        }
      });

      return filtered;
    },
  },
  watch: {
    isPrinting(isPrinting) {
      if (
        !isPrinting ||
        !this.isActiveTab ||
        !this.filteredData ||
        Object.keys(this.filteredData).length === 0
      ) {
        return;
      }

      this.printReportTable();
    },
    isExcelDownloading(isDownloading) {
      if (
        !isDownloading ||
        !this.isActiveTab ||
        !this.filteredData ||
        Object.keys(this.filteredData).length === 0
      ) {
        return;
      }

      this.exportReportExcel();
    },
    dateRange: {
      handler(newVal, oldVal) {
        if (newVal && newVal.length >= 2) {
          this.loadReportData();
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    calculateTotal(item) {
      if (!item.orders_guest || !item.orders_guest.length) return 0;

      let total = 0;
      item.orders_guest.forEach((order) => {
        if (order.order_guest_transactions) {
          order.order_guest_transactions.forEach((transaction) => {
            total += transaction.price * transaction.quantity;
          });
        }
      });

      return total;
    },
    showOrderDetails(order, guest) {
      this.selectedOrder = order;
      this.selectedGuest = guest;
      this.orderDetailsDialog = true;
    },
    async changeOrderStatus(order, guest, newStatus) {
      if (order.state === newStatus) return;

      this.changingStatusOrderId = order.id;
      this.isLoading = true;

      // Send the entire order object with updated status
      const payload = {
        ...order,
        order_id: order.id,
        user_id: guest.id,
        status: newStatus,
      };

      try {
        await reportService.changeOrderStatus(payload);
        // Update local state
        order.state = newStatus;
        if (order.order_guest_status) {
          order.order_guest_status.status = newStatus;
        }
        this.showSuccessSnackbar(this.$t("status_changed_successfully"));
        this.$emit("status-changed", { order, newStatus });
      } catch (error) {
        console.error("Error changing order status:", error);
        this.showErrorSnackbar(this.$t("status_change_error"));
        this.$emit("error", error);
      } finally {
        this.changingStatusOrderId = null;
        this.isLoading = false;
      }
    },
    loadReportData() {
      if (!this.dateRange || this.dateRange.length < 2) return;

      const params = {
        date: this.dateRange[0],
        date2: this.dateRange[1],
      };

      this.isLoading = true;

      reportService
        .getNoContractDelivery(this.$store.state.restaurant.id, params)
        .then((response) => {
          this.reportData = response.data;
        })
        .catch((error) => {
          console.error("Error loading report data:", error);
          this.reportData = {};
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    printReportTable() {
      const tableItems = [];

      Object.keys(this.filteredData).forEach((date) => {
        const guests = this.filteredData[date];
        guests.forEach((guest) => {
          const total = this.calculateTotal(guest);
          const order =
            guest.orders_guest && guest.orders_guest[0] ? guest.orders_guest[0] : {};

          tableItems.push({
            num: order.id || "",
            nameAndType: `${guest.name} ${guest.lastname}\n${guest.phone}`,
            address: this.getAddressText(order),
            time: order.delivery_time ? order.delivery_time.substring(0, 5) : "",
            orders: this.getOrdersText(guest),
            total: total.toFixed(1),
            status: this.$t(order.state || ""),
          });
        });
      });

      this.printDocument({
        pageTitle: this.$t("guest_report"),
        title: this.$t("guest_report"),
        subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
          this.dateRange[1]
        )}`,
        tables: [
          {
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
    exportReportExcel() {
      const tableItems = [];

      Object.keys(this.filteredData).forEach((date) => {
        const guests = this.filteredData[date];
        guests.forEach((guest) => {
          const total = this.calculateTotal(guest);
          const order =
            guest.orders_guest && guest.orders_guest[0] ? guest.orders_guest[0] : {};

          tableItems.push({
            num: order.id || "",
            nameAndType: `${guest.name} ${guest.lastname}\n${guest.phone}`,
            address: this.getAddressText(order),
            time: order.delivery_time ? order.delivery_time.substring(0, 5) : "",
            orders: this.getOrdersText(guest),
            total: total.toFixed(1),
            status: this.$t(order.state || ""),
          });
        });
      });

      this.downloadExcel({
        fileName: `izvestaj_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`,
        sheets: [
          {
            name: this.$t("guest_report"),
            title: this.$t("guest_report"),
            subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
              this.dateRange[1]
            )}`,
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
    getAddressText(order) {
      if (!order) return "";

      if (order.delivery_type === 0) {
        return `${this.$t("delivery_to_address")}\n${order.location?.address || ""}`;
      } else if (order.delivery_type === 1) {
        return `${this.$t("pickup")}\n${this.$t("in_restaurant")}`;
      } else if (order.delivery_type === 2) {
        return this.$t("consumption_in_restaurant");
      }
      return "";
    },
    getOrdersText(guest) {
      if (!guest.orders_guest || !guest.orders_guest.length) return "";

      let text = "";
      guest.orders_guest.forEach((order) => {
        if (order.order_guest_transactions) {
          order.order_guest_transactions.forEach((transaction) => {
            text += `${transaction.quantity}x ${transaction.food?.name || ""}: ${(
              transaction.price * transaction.quantity
            ).toFixed(1)}\n`;
          });
        }
      });
      return text.trim();
    },
  },
};
</script>
